import { useEffect, useRef } from 'react';

import { schemas } from '@recurrency/core-api-schema';
import {
  SpecialOrderFilterOptions,
  SupplierTargetTypes,
  UsageQuantitySource,
} from '@recurrency/core-api-schema/dist/common/enums';
import { PurchaseGroupWithTargetDTO } from '@recurrency/core-api-schema/dist/purchaseGroups/getPurchaseGroups';
import { PurchaseTargetDTO } from '@recurrency/core-api-schema/dist/purchasing/getPurchaseTargets';
import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { theme } from 'theme';

import { MultiSelectOption } from 'components/AsyncSelect/types';
import { calcOp, calcProp } from 'components/recipes/equation/Equation';
import { SpokeRequiredQtyRecord } from 'components/recipes/equation/QtyToOrderPopover/types';
import { SupplierLocationSettingsModal } from 'components/recipes/SupplierLocationSettingsModal';
import { SelectOption } from 'components/Select';

import { useCoreApi } from 'hooks/useApi';
import { ExtendedUser } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { formatNumber, roundTo2Decimals } from 'utils/formatting';
import { LocalStorageKey, useLocalStorage } from 'utils/localStorage';
import { isObjEmpty, objFilterValues } from 'utils/object';
import { useHashState } from 'utils/routes';

import { SavedFilterData, SearchFrameHashState, SupplierLocationWTarget } from 'types/hash-state';

export const USAGE_COLOR = theme.colors.neutral[300];
export const INHERITED_COLOR = theme.colors.neutral[400];
export const FORECAST_COLOR = theme.colors.chart.sunsetPurple;
export const BASE_FORECAST_COLOR = theme.colors.chart.malibuOrange;

export const UsageSourceDisplayName: Record<UsageQuantitySource, string> = {
  [UsageQuantitySource.QuantityOrdered]: 'sales order',
  [UsageQuantitySource.QuantityInvoiced]: 'invoice',
};

export const productTypeFacetOptions = [
  { label: 'Regular', value: 'Regular' },
  { label: 'Temporary', value: 'Temporary' },
] as const;

export const sharedFilters = [
  'location',
  'purchase_class',
  'purchase_class_and_description',
  'primary_vendor',
  'primary_supplier',
  'item_group',
  'product_type',
  'demand_pattern',
  'predictability_tag',
];
// Deprecate special order filter after GA of priority disp filter
export const specialOrderFilterOptions: MultiSelectOption[] = [
  { label: 'POs With Special Orders', value: SpecialOrderFilterOptions.POsWithSpecialOrders },
  { label: 'POs Without Special Orders', value: SpecialOrderFilterOptions.POsWithoutSpecialOrders },
];

export enum ReplenishmentMethod {
  OPOQ = 'OP/OQ',
  MinMax = 'Min/Max',
  EOQ = 'EOQ',
  UpTo = 'Up To',
}

export function useDemandPlanningFilterSave() {
  const [hashState, updateHashState] = useHashState<SearchFrameHashState>();
  const [state, setState] = useLocalStorage(LocalStorageKey.Demand_Planning_Filters, hashState);
  // On load check if hash state is empty and if so, replace it
  useEffect(() => {
    if (isObjEmpty(hashState)) {
      updateHashState(state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every time hash state changes, update local storage by storing the saved filters and query
  useEffect(() => {
    setState({
      where: objFilterValues(hashState.where || {}, (_, key) => sharedFilters.includes(key)),
      query: hashState.query,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashState]);
}

/** Hash-state keys persisted in a Planning saved filter's `data` snapshot. */
export const SAVED_FILTER_KEYS = ['where', 'extFilter', 'query', 'sortBy', 'secondarySortBy'] as const;

/**
 * Extract saved filter data from hash/view state, dropping nil/empty values so two
 * equivalent states compare equal (used for dirty detection and for applying a saved filter).
 */
export function getSavedFilterData(state: Partial<SearchFrameHashState> | undefined): SavedFilterData {
  const snapshot: SavedFilterData = {};
  for (const key of SAVED_FILTER_KEYS) {
    const value = state?.[key];
    const isEmpty =
      value == null ||
      (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) ||
      (typeof value === 'string' && value.length === 0);
    if (!isEmpty) {
      // casting to satisfy lint
      (snapshot as Record<string, unknown>)[key] = value;
    }
  }
  return snapshot;
}

/**
 * Hashstate bootstrap
 */
export function usePlanningFilterBootstrap() {
  const [hashState, updateHashState] = useHashState<SearchFrameHashState>();
  const [localFilters, setLocalFilters] = useLocalStorage(LocalStorageKey.Demand_Planning_Filters, hashState);
  // tracks if hash state is boot strapped
  const isBootStrappedRef = useRef(false);
  // tracks if hash state was originally empty (not a deep link)
  const wasInitiallyEmptyRef = useRef(isObjEmpty(hashState));

  const { isLoading, error, data: savedFilters } = useCoreApi(schemas.savedFilters.getSavedFilters, {});

  useEffect(() => {
    if (isBootStrappedRef.current) return;
    // Preserve an incoming deep-link hash — never override it with a default/localStorage restore.
    if (!wasInitiallyEmptyRef.current) {
      isBootStrappedRef.current = true;
      return;
    }
    // Wait for the request to settle (success or error) before deciding.
    if (isLoading) return;
    // On success apply the default if one exists; on error (savedFilters undefined) or no default,
    // fall back to the localStorage filters.
    const defaultFilter = savedFilters?.items.find((filter) => filter.isDefault);
    if (defaultFilter) {
      updateHashState({
        where: undefined,
        extFilter: undefined,
        query: undefined,
        sortBy: undefined,
        secondarySortBy: undefined,
        ...getSavedFilterData(defaultFilter.data),
        page: 0,
      });
    } else {
      // No server default (or the request failed) — fall back to the localStorage filters.
      updateHashState(localFilters);
    }
    isBootStrappedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters, isLoading, error]);

  // Update localstorage filters. same logic as forecasting page
  useEffect(() => {
    setLocalFilters({
      where: objFilterValues(hashState.where || {}, (_, key) => sharedFilters.includes(key)),
      query: hashState.query,
    });
  }, [hashState, setLocalFilters]);
}

/** discountPercent = ((originalCost - cost) / original) * 100 */
export const calcDiscountPercent = (originalCost: number, cost: number) =>
  originalCost === 0 ? 0 : roundTo2Decimals(((originalCost - cost) / originalCost) * 100);

export const shouldShowOpOqVariant = (
  activeTenant: TenantDTO,
  activeUser: ExtendedUser,
  replenishmentMethod: string | undefined,
) => replenishmentMethod === ReplenishmentMethod.OPOQ;

export function recordToSupplierLocationWTarget(record: PurchaseTargetDTO): SupplierLocationWTarget {
  return {
    supplierId: record.supplierId,
    supplierName: record.supplierName,
    locationId: record.purchaseLocationId,
    locationName: record.purchaseLocationName,
    vendorId: record.vendorId,
    vendorName: record.vendorName,
    // sapb1 tenants don't yet have target types, so default to dollars so lines page can be browsed
    targetValue: record.targetValue ?? 0,
    targetType: record.targetType ?? SupplierTargetTypes.Dollars,
  };
}

export function groupRecordToSupplierLocationsWTargets(
  group: PurchaseGroupWithTargetDTO & { purchaseTargets: PurchaseTargetDTO[] },
): SupplierLocationWTarget[] {
  const spokeSupplierLocations = group.purchaseTargets.map((target) => ({
    supplierId: group.supplierId,
    supplierName: group.supplierName,
    locationId: target.purchaseLocationId,
    locationName: target.purchaseLocationName,
    vendorId: group.vendorId ?? '',
    vendorName: group.vendorName ?? '',
    purchasingLocationId: group.purchaseLocationId,
    purchasingLocationName: group.purchaseLocationName,
    targetType: target.targetType || SupplierTargetTypes.Dollars,
    targetValue: target.targetValue || 0,
    groupName: group.groupName,
    groupId: group.groupId,
  }));

  const hubSupplierLocation = {
    supplierId: group.supplierId,
    supplierName: group.supplierName,
    locationId: group.purchaseLocationId,
    locationName: group.purchaseLocationName,
    vendorId: group.vendorId ?? '',
    vendorName: group.vendorName ?? '',
    purchasingLocationId: group.purchaseLocationId,
    purchasingLocationName: group.purchaseLocationName,
    targetType: group.targetType || SupplierTargetTypes.Dollars,
    targetValue: group.targetValue || 0,
    groupName: group.groupName,
    groupId: group.groupId,
  };

  return [hubSupplierLocation, ...spokeSupplierLocations];
}

export function getMatchingSupplierAndLocation(groupIds: string[], groups: PurchaseGroupWithTargetDTO[]) {
  const supplierLocationMap = new Map<string, string>();

  for (const groupId of groupIds) {
    const group = groups.find((g) => g.groupId === groupId);

    if (group) {
      // Create a unique key for each combination of supplierId and locationId
      const { supplierId } = group;
      const locationIds = [group.purchaseLocationId, ...(group.spokeLocations?.map((loc) => loc.foreignId) || [])];

      for (const locationId of locationIds) {
        const key = `${supplierId}:${locationId}`;

        if (supplierLocationMap.has(key)) {
          // If the key already exists, we have a match

          const locationName =
            group.purchaseLocationId === locationId
              ? group.purchaseLocationName
              : group.spokeLocations?.find((sl) => sl.foreignId === locationId)?.name;

          return {
            hasMatch: true,
            locationName,
            supplierName: group.supplierName,
            groupName1: group.groupName,
            groupName2: supplierLocationMap.get(key),
          };
        }
        // Otherwise, store the groupId with this unique key
        supplierLocationMap.set(key, group.groupName);
      }
    }
  }

  return { hasMatch: false };
  // return false;
}

export const TargetTypeLabels: Record<SupplierTargetTypes, string> = {
  [SupplierTargetTypes.Dollars]: 'Dollars',
  [SupplierTargetTypes.Units]: 'Units',
  [SupplierTargetTypes.Weight]: 'Weight',
  [SupplierTargetTypes.Volume]: 'Volume',
  [SupplierTargetTypes.NotSet]: 'None',
};

export const TargetTypesFilterOptions: MultiSelectOption[] = Object.entries(TargetTypeLabels).map(([value, label]) => ({
  label,
  value,
}));

export const TargetTypeSelectOptions: SelectOption[] = Object.entries(SupplierTargetTypes)
  .map(([key, value]) => ({
    value,
    label: key.toUpperCase(),
  }))
  .filter((option) => option.value !== SupplierTargetTypes.NotSet);

export const editButtonOnClick = async (supplierId: string, locationId: string, onChange: () => void) => {
  const newSettings = await showAsyncModal(SupplierLocationSettingsModal, {
    supplierId,
    locationId,
  });
  if (newSettings) {
    onChange();
  }
};

export const getSpokeRequiredQtyLines = (record: SpokeRequiredQtyRecord) => {
  // Default calculation for min/max for understock items, this is to make the tooltips math add up
  // This is a temporary measure and just made to have the math "add up" since we don't surface the
  // actual spoke needs in the transform
  let spokeRequiredQty = record.qtyToOrder - record.currentMax + record.netStock;
  // Items can be NS at hub, but stockable at spokes
  if (!record.stockable) spokeRequiredQty = record.qtyToOrder - record.qtyBackordered;

  // Only show the spoke requirements if they are non-zero
  if (spokeRequiredQty === 0) return [];

  return [
    calcOp('+'),
    calcProp('Spoke Requirements', `${formatNumber(spokeRequiredQty)} ${record.unitOfMeasure || ''}`),
  ];
};
